import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  Header,
  Pagination,
  PropertyFilter,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@amzn/awsui-components-react-v3';

import {
  AMAZON_EMPLOYEE,
  CONSUMER,
  CUSTODIAN,
  GLADSTONE_GROUP,
  GLADSTONE_WORKSPACE,
  RUNNING,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import { Link } from 'src/components/common/Link';
import { generateLinkForReviewer } from 'src/commons/common';
import { createReviewDetailsLink } from 'src/routes';
import { ReviewStatus } from 'src/components/reviews/reviewStatus';
import { listReviews, listSeasons } from 'src/api/notifications';
import { StatusIndicatorProps } from '@amzn/awsui-components-react-v3/polaris/status-indicator/interfaces';
import { EmptyState } from 'src/commons/EmptyState';
import { defaultWrapLinesPreference, i18nStrings, largePageSizePreference, paginationLabels } from 'src/commons/tables';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { convertUTCToLocalDate, getAgreementDifference } from 'src/components/reviews/utils';
import { ReviewAgreementDataDiffModal } from 'src/components/reviews/reviewAgreementDataDiffModal';
import { ReviewStatusChartModal } from 'src/components/reviews/reviewStatusChartModal';

export interface AccessReviewHistoryPageProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  userInfo: any;
  match: any;
}

export const AccessReviewHistoryPage = (props: AccessReviewHistoryPageProps) => {
  const [allItems, setItems] = useState([]);
  const [reviewTableLoading, setReviewTableLoading] = useState(false);
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [agreementDifferencesModalVisible, setAgreementDifferencesModalVisible] = useState(false);
  const [agreementDifferences, setAgreementDifferences] = useState(null);
  const [agreementValidationResult, setAgreementValidationResult] = useState<string>(undefined);
  const [selectedSeason, setSelectedSeason] = useState(undefined);
  const [reviewStatusChartModalVisible, setReviewStatusChartModalVisible] = useState(false);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });

  const fetchAllReviewSeasons = async () => {
    try {
      const listSeasonsResponse = await listSeasons({});
      let seasons = [];
      listSeasonsResponse.seasons
        ?.sort((a, b) => b.reviewStartDate.localeCompare(a.reviewStartDate))
        .forEach((season) => {
          let isRunning = RUNNING == season.status;
          let color: StatusIndicatorProps.Color = isRunning ? 'green' : 'blue';
          let type: StatusIndicatorProps.Type = isRunning ? 'in-progress' : 'stopped';
          seasons.push({
            label: (
              <StatusIndicator colorOverride={color} type={type}>
                {season.name}
              </StatusIndicator>
            ),
            description:
              convertUTCToLocalDate(season.reviewStartDate) + ' - ' + convertUTCToLocalDate(season.finalEndDate),
            value: season.seasonId,
          });
        });

      if (seasons.length > 0) {
        setSelectedSeason(seasons[0]);
      }
      setSeasonOptions(seasons);
    } catch (e) {
      console.error('Failed to review seasons with error: ', e);
    }
  };

  const handleRefresh = async () => {
    await fetchAllReviewSeasons();
  };

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
  }, []);

  const columnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'responseDiff',
      header: 'Review response',
      cell: (item) =>
        item.responseDiff == null ? (
          '-'
        ) : (
          <Link
            onClick={() => {
              setAgreementDifferencesModalVisible(true);
              setAgreementDifferences(item.responseDiff);
              setAgreementValidationResult(item.agreementValidationResult);
            }}
          >
            View response diff
          </Link>
        ),
      minWidth: 150,
    },
    {
      id: 'reviewDetail',
      header: 'Review detail',
      cell: (item) => (
        <Link href={createReviewDetailsLink(item.reviewId)} external={true}>
          {item.reviewId}
        </Link>
      ),
      minWidth: 150,
      sortingField: 'reviewDetail',
    },
    {
      id: 'resourceType',
      header: 'Resource type',
      cell: (item) => item.resourceType,
      minWidth: 150,
      sortingField: 'resourceType',
    },
    {
      id: 'resourceId',
      header: 'Resource ID',
      cell: (item) => (
        <Link external={true} href={item.link}>
          {item.name || item.resourceId}
        </Link>
      ),
      minWidth: 150,
      sortingField: 'resourceId',
    },
    {
      id: 'description',
      header: 'Resource description',
      cell: (item) => item.description,
      minWidth: 150,
      sortingField: 'description',
    },
    {
      id: 'reviewedBy',
      header: 'Reviewed by',
      cell: (item) =>
        item.reviewedBy == null ? (
          '-'
        ) : (
          <Link href={generateLinkForReviewer(item.reviewedBy, AMAZON_EMPLOYEE)} external={true}>
            {item.reviewedBy}
          </Link>
        ),
      minWidth: 150,
      sortingField: 'reviewedBy',
    },
    {
      id: 'submittedAt',
      header: 'Reviewed at',
      cell: (item) => (item.reviewedAt == null ? '-' : convertUTCToLocalDate(item.reviewedAt)),
      minWidth: 150,
      sortingField: 'submittedAt',
    },
    {
      id: 'tag',
      header: 'Tag',
      cell: (item) => (item.tag == null ? '-' : item.tag),
      minWidth: 150,
      sortingField: 'tag',
    },
    {
      id: 'action',
      header: 'Action',
      cell: (item) => (item.action == null ? '-' : item.action),
      minWidth: 150,
      sortingField: 'action',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item) => ReviewStatus(item),
      minWidth: 150,
      sortingField: 'status',
    },
  ];

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  const closeAgreementDifferenceModal = () => {
    setAgreementDifferencesModalVisible(false);
  };

  const closeReviewStatusChartModal = () => {
    setReviewStatusChartModalVisible(false);
  };

  const listAllReviews = async (seasonId: string, reviewerType: string) => {
    setReviewTableLoading(true);
    try {
      const requests = [];
      // Midway reviews
      requests.push(getReviews({ seasonId: seasonId }));

      // Group reviews
      props.userInfo?.memberGroupIds?.forEach((groupId) => {
        requests.push(
          getReviews({
            seasonId: seasonId,
            reviewerType: GLADSTONE_GROUP,
            reviewerId: groupId,
          }),
        );
      });

      // Workspace reviews
      props.userInfo?.memberWorkspaceIds?.forEach((workspaceId) => {
        requests.push(
          getReviews({
            seasonId: seasonId,
            reviewerType: GLADSTONE_WORKSPACE,
            reviewerId: workspaceId,
          }),
        );
      });

      const results = await Promise.all(requests);

      let items = results
        .flat()
        .filter((res) => res.reviewerPersona == reviewerType)
        .map((review) => {
          const resourceData = JSON.parse(review.resourceData);
          const tag = resourceData['Governance tags'] ?? null;

          const resourceAgreementsBefore = review.resourceAgreementsBefore;
          const resourceAgreements = review.latestConsumerReviewRecord?.resourceAgreements;
          let responseDiff = null;
          if (resourceAgreements != null) {
            responseDiff = getAgreementDifference(resourceAgreements, resourceAgreementsBefore);
          }
          return {
            seasonId: review.seasonId,
            resourceId: review.resourceId,
            namespace: review.namespace,
            resourceType: review.resourceType,
            reviewId: review.reviewId,
            reviewedAt: review.latestConsumerReviewRecord?.reviewedAt,
            reviewedBy: review.latestConsumerReviewRecord?.reviewedBy,
            agreementValidationResult: review.latestConsumerReviewRecord?.agreementValidationResult,
            status: review.reviewStatus,
            link: review.link,
            description: review.description,
            action: review.latestConsumerReviewRecord?.action,
            tag: tag,
            responseDiff: responseDiff,
          };
        });
      setItems(items);
    } catch (e) {
      console.log('Failed to load history reviews', e);
      setReviewTableLoading(false);
    }
    setReviewTableLoading(false);
  };

  const getReviews = async (request) => {
    let result = [];
    let nextToken = null;
    try {
      do {
        const res = await listReviews(request);
        result.push(...res.reviews);
        nextToken = res.nextToken;
        request.nextToken = nextToken;
      } while (nextToken != null);
    } catch (e) {
      console.log(e);
    }
    return result;
  };

  useEffect(() => {
    if (selectedSeason != null && selectedSeason.value != null) {
      let reviewerType = props.match.params.reviewerType;
      if (reviewerType == 'custodian') {
        listAllReviews(selectedSeason.value, CUSTODIAN);
      } else if (reviewerType == 'consumer') {
        listAllReviews(selectedSeason.value, CONSUMER);
      } else {
        console.log('Invalid reviewerType in URL');
      }
    }
  }, [selectedSeason]);

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount } = useCollection(allItems, {
    propertyFiltering: {
      filteringProperties: [
        {
          propertyLabel: 'Review detail',
          key: 'reviewId',
          groupValuesLabel: 'Review details',
        },
        {
          propertyLabel: 'Status',
          key: 'status',
          groupValuesLabel: 'Status',
        },
        {
          propertyLabel: 'Resource type',
          key: 'resourceType',
          groupValuesLabel: 'Resource types',
        },
        {
          propertyLabel: 'Resource description',
          key: 'description',
          groupValuesLabel: 'Resource descriptions',
        },
        {
          propertyLabel: 'Tag',
          key: 'tag',
          groupValuesLabel: 'Tags',
        },
        {
          propertyLabel: 'Resource id',
          key: 'resourceId',
          groupValuesLabel: 'Resource ids',
        },
      ],
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {},
  });

  return (
    <>
      <Table
        {...collectionProps}
        columnDefinitions={columnDefinitions}
        loading={reviewTableLoading}
        header={
          <Header
            actions={
              <SpaceBetween direction='horizontal' size='s'>
                {!reviewTableLoading && 'custodian' == props.match.params.reviewerType && allItems.length > 0 && (
                  <Button variant='normal' onClick={() => setReviewStatusChartModalVisible(true)}>
                    Check review status dashboard
                  </Button>
                )}
                <Select
                  filteringType='none'
                  inlineLabelText='Season'
                  options={seasonOptions}
                  placeholder='Choose season'
                  selectedOption={selectedSeason}
                  triggerVariant='label'
                  onChange={(e) => setSelectedSeason(e.detail.selectedOption)}
                />
              </SpaceBetween>
            }
            counter={`(${items.length})`}
            variant='awsui-h1-sticky'
          >
            Review history
          </Header>
        }
        items={items}
        empty={<EmptyState title={selectedSeason == null ? 'Select a review season' : 'No reviews'} subtitle={''} />}
        pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            disabled={reviewTableLoading}
            i18nStrings={i18nStrings}
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
          />
        }
        preferences={
          <CollectionPreferences
            title={'Preferences'}
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={largePageSizePreference}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
        resizableColumns
        stickyHeader
        variant='full-page'
        trackBy='id'
        data-venue='true'
      />

      <ReviewAgreementDataDiffModal
        visible={agreementDifferencesModalVisible}
        dismiss={closeAgreementDifferenceModal}
        diff={agreementDifferences}
        agreementValidationResult={agreementValidationResult}
        {...props}
      />

      {selectedSeason && allItems && (
        <ReviewStatusChartModal
          visible={reviewStatusChartModalVisible}
          dismiss={closeReviewStatusChartModal}
          reviews={allItems}
          reviewSeason={selectedSeason.value}
          {...props}
        />
      )}
    </>
  );
};
